import { useCallback, useEffect, useState } from "react";
import InputField from "components/fields/InputField";
import StatusBadge from "components/admin/StatusBadge";
import { apiGet, apiPost } from "lib/apiClient";

function mfaError(err, fallback) {
  switch (err?.code) {
    case "MFA_CODE_INVALID":
      return "That code is not right. Check it and try again.";
    case "MFA_CODE_EXPIRED":
      return "That code has expired. Send a new one.";
    case "MFA_CHALLENGE_INVALID":
    case "MFA_TOO_MANY_ATTEMPTS":
      return "That code can no longer be used. Start again to get a new one.";
    case "MFA_RATE_LIMITED":
      return "Codes were requested too often. Wait a minute, then try again.";
    case "MFA_DESTINATION_REQUIRED":
      return "Enter the phone number that should receive your codes.";
    case "CURRENT_PASSWORD_INCORRECT":
      return "That is not your current password.";
    case "PASSWORD_CHANGE_REQUIRED":
      return "Set your own password first, then turn on two-factor verification.";
    default:
      return err?.message || fallback;
  }
}

const METHOD_LABEL = { EMAIL: "Email", SMS: "SMS" };

export default function TwoFactorSettings({ defaultPhone }) {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [mode, setMode] = useState("idle");
  const [method, setMethod] = useState("EMAIL");
  const [phone, setPhone] = useState(defaultPhone || "");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [currentCode, setCurrentCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      setStatus(await apiGet("/auth/mfa"));
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message || "Could not load your two-factor settings.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function reset(nextNotice = null) {
    setMode("idle");
    setChallenge(null);
    setCode("");
    setCurrentCode("");
    setPassword("");
    setError(null);
    setNotice(nextNotice);
  }

  async function sendCode() {
    setError(null);
    setNotice(null);
    if (method === "SMS" && phone.trim().replace(/\D/g, "").length < 9) return setError("Enter the phone number that should receive your codes.");
    if (status?.enabled && !password) return setError("Enter your current password.");
    setBusy(true);
    try {
      const result = await apiPost("/auth/mfa/enroll", { method, ...(method === "SMS" ? { phone: phone.trim() } : {}), ...(status?.enabled ? { currentPassword: password } : {}) });
      setChallenge(result);
      setCode("");
      setCurrentCode("");
      setMode("confirm");
    } catch (err) {
      setError(mfaError(err, "Could not send a code."));
    } finally {
      setBusy(false);
    }
  }

  async function confirmCode() {
    setError(null);
    if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code.");
    if (challenge.currentFactor && !/^\d{6}$/.test(currentCode)) return setError("Enter the 6-digit code sent to your current method.");
    setBusy(true);
    try {
      const next = await apiPost("/auth/mfa/enroll/confirm", { challengeToken: challenge.challengeToken, code, ...(challenge.currentFactor ? { currentCode } : {}) });
      setStatus(next);
      reset("Two-factor verification is on. You will be asked for a code each time you sign in.");
    } catch (err) {
      if (err?.code === "MFA_CHALLENGE_INVALID" || err?.code === "MFA_TOO_MANY_ATTEMPTS") {
        setChallenge(null);
        setMode("enroll");
      }
      setError(mfaError(err, "Could not confirm the code."));
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setError(null);
    if (!password) return setError("Enter your current password.");
    setBusy(true);
    try {
      const next = await apiPost("/auth/mfa/disable", { currentPassword: password });
      setStatus(next);
      reset("Two-factor verification is off.");
    } catch (err) {
      setError(mfaError(err, "Could not turn off two-factor verification."));
    } finally {
      setBusy(false);
    }
  }

  const primaryButton = "rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50";
  const secondaryButton = "rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Two-factor verification</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">After your password, sign-in also asks for a 6-digit code sent to your email or phone.</p>
        </div>
        {status && <StatusBadge label={status.enabled ? `On (${METHOD_LABEL[status.method] || status.method})` : "Off"} tone={status.enabled ? "green" : "gray"} />}
      </div>

      {loadError && <p className="mt-3 text-sm font-medium text-red-500">{loadError}</p>}
      {notice && <p className="mt-3 text-sm font-medium text-green-600">{notice}</p>}

      {status?.enabled && mode === "idle" && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Codes go to <span className="font-semibold text-navy-700 dark:text-white">{status.destinationHint}</span>.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={() => { reset(); setMode("enroll"); }} className={secondaryButton}>
              Change method
            </button>
            <button type="button" onClick={() => { reset(); setMode("disable"); }} className="rounded-sm bg-red-50 px-4 py-2 text-sm font-semibold text-red-500">
              Turn off
            </button>
          </div>
        </div>
      )}

      {status && !status.enabled && mode === "idle" && (
        <button type="button" onClick={() => { reset(); setMode("enroll"); }} className={`mt-4 ${primaryButton}`}>
          Turn on two-factor verification
        </button>
      )}

      {mode === "enroll" && (
        <div className="mt-4">
          <p className="mb-2 ml-3 text-sm font-semibold text-navy-700 dark:text-white">Send codes by</p>
          <div className="flex gap-2">
            {["EMAIL", "SMS"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMethod(m)}
                aria-pressed={method === m}
                className={`rounded-sm px-4 py-2 text-sm font-semibold ${method === m ? "bg-brand-500 text-white" : "border border-gray-200 text-navy-700 dark:border-white/10 dark:text-white"}`}
              >
                {METHOD_LABEL[m]}
              </button>
            ))}
          </div>
          {method === "SMS" ? (
            <InputField extra="mt-4" label="Phone number that will receive codes" placeholder="07XX XXX XXX" id="mfa-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          ) : (
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Codes go to the email address you sign in with.</p>
          )}
          {status?.enabled && (
            <InputField extra="mt-4" label="Current password" id="mfa-change-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
          {error && <p className="mt-3 text-sm font-medium text-red-500">{error}</p>}
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={sendCode} disabled={busy} className={primaryButton}>
              {busy ? "Sending…" : "Send code"}
            </button>
            <button type="button" onClick={() => reset()} className={secondaryButton}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === "confirm" && challenge && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            We sent a 6-digit code to <span className="font-semibold text-navy-700 dark:text-white">{challenge.destinationHint}</span>. Enter it to finish.
          </p>
          <InputField
            extra="mt-3"
            label="Verification code"
            placeholder="123456"
            id="mfa-enroll-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          {challenge.currentFactor && (
            <>
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
                To confirm the change, we also sent a code to your current method, <span className="font-semibold text-navy-700 dark:text-white">{challenge.currentFactor.destinationHint}</span>.
              </p>
              <InputField
                extra="mt-3"
                label="Code from your current method"
                placeholder="123456"
                id="mfa-current-code"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                value={currentCode}
                onChange={(e) => setCurrentCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </>
          )}
          {error && <p className="mt-3 text-sm font-medium text-red-500">{error}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={confirmCode} disabled={busy} className={primaryButton}>
              {busy ? "Checking…" : "Confirm"}
            </button>
            <button type="button" onClick={sendCode} disabled={busy} className={secondaryButton}>
              Send a new code
            </button>
            <button type="button" onClick={() => reset()} className={secondaryButton}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === "disable" && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">Confirm with your current password. Sign-in will then need only your password.</p>
          <InputField extra="mt-3" label="Current password" id="mfa-disable-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <p className="mt-3 text-sm font-medium text-red-500">{error}</p>}
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={disable} disabled={busy} className="rounded-sm bg-red-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? "Turning off…" : "Turn off"}
            </button>
            <button type="button" onClick={() => reset()} className={secondaryButton}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
